import React, { useCallback, useEffect, useRef, useState } from 'react';
import { dashboardAPI } from '../../utils/api';
import { getEventTitle } from '../../utils/modelLabels';
import { getModelIconName, modelIconUrl } from '../../utils/modelIcons';
import '../../pages/Area/Area.css';
import './EventsModal.css';

export type DashboardEvent = Awaited<ReturnType<typeof dashboardAPI.getRecentEvents>>['events'][number];

interface Props {
  onClose: () => void;
  onOpenEvent: (event: DashboardEvent) => void;
  viewedLogIds: Set<number>;
  photoOpen?: boolean;
}

const eventDate = (timestamp: string | null) => {
  const date = timestamp ? new Date(timestamp.replace(' ', 'T')) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
};

const EventsModal: React.FC<Props> = ({ onClose, onOpenEvent, viewedLogIds, photoOpen = false }) => {
  const [events, setEvents] = useState<DashboardEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState<number | null>(null);
  const limitRef = useRef(0);
  const inFlight = useRef(false);
  const mounted = useRef(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  const load = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    const limit = limitRef.current === 0 ? 6 : Math.min(1000, limitRef.current + 3);
    setLoading(true);
    setError('');
    try {
      const response = await dashboardAPI.getRecentEvents({ limit, offset: 0 });
      if (!response.success) throw new Error('Не удалось загрузить события. Попробуйте ещё раз.');
      if (!mounted.current) return;
      // Это обновлённый срез с offset=0, а не следующая страница: заменяем, не дублируем.
      setEvents(response.events);
      setHasMore(response.has_more);
      setTotal(response.total);
      limitRef.current = limit;
    } catch (err) {
      if (mounted.current) setError(err instanceof Error ? err.message : 'Не удалось загрузить события. Попробуйте ещё раз.');
    } finally {
      inFlight.current = false;
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void load();
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    return () => {
      mounted.current = false;
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, [load]);

  useEffect(() => {
    if (photoOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); closeRef.current(); }
      if (event.key !== 'Tab') return;
      const buttons = dialogRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), [tabindex="0"]');
      if (!buttons?.length) return;
      const first = buttons[0];
      const last = buttons[buttons.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [photoOpen]);

  return <div className="area-modal events-modal" role="dialog" aria-modal="true" aria-labelledby="events-modal-title">
    <div className="area-modal__backdrop" onClick={onClose} />
    <div className="area-modal__dialog events-modal__dialog" ref={dialogRef} tabIndex={-1}>
      <header className="area-modal__header">
        <div><span className="events-modal__eyebrow">Журнал детекций</span>
          <h2 id="events-modal-title" className="area-modal__title">Все события</h2>
          <p className="events-modal__subtitle">С доступных камер · сначала новые</p></div>
        <button type="button" className="area-modal__close" aria-label="Закрыть журнал" onClick={onClose}>×</button>
      </header>
      <div className="events-modal__body" aria-busy={loading}>
        {loading && events.length === 0 && <div className="ui-loading" role="status">Загрузка событий…</div>}
        {!loading && !error && events.length === 0 && <div className="ui-empty"><strong>{hasMore ? 'В этой выборке нет доступных событий' : 'Событий пока нет'}</strong><span>{hasMore ? 'Нажмите «Показать ещё», чтобы загрузить более ранние события' : 'Здесь появятся детекции с доступных камер'}</span></div>}
        <ul className="events-modal__list">
          {events.map((event, index) => {
            const date = eventDate(event.timestamp);
            const dateLabel = date?.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }) ?? 'Дата не указана';
            const previousDate = index > 0 ? eventDate(events[index - 1].timestamp)?.toDateString() : undefined;
            const viewed = viewedLogIds.has(event.id);
            return <li key={event.id}>
              {(index === 0 || date?.toDateString() !== previousDate) && <div className="events-modal__date">{dateLabel}</div>}
              <button type="button" className="events-modal__event" onClick={() => onOpenEvent(event)}>
                <span className="ui-icon-tile events-modal__icon" aria-hidden="true"><img src={modelIconUrl(getModelIconName(event.model))} alt="" /></span>
                <span className="events-modal__event-content"><strong>{getEventTitle(event.model)}</strong>
                  <span><span>{event.area_name}</span> <span aria-hidden="true">·</span> <span>{event.camera_name}</span></span></span>
                <span className="events-modal__event-end"><time dateTime={event.timestamp ?? undefined}>{date?.toLocaleTimeString('ru-RU') ?? '—'}</time>
                  <span className={`events-modal__badge ${viewed ? 'events-modal__badge--viewed' : ''}`}>{viewed ? 'Просмотрено' : 'Новое'}</span></span>
                <span className="events-modal__arrow" aria-hidden="true">›</span>
              </button>
            </li>;
          })}
        </ul>
        {error && <div className="events-modal__error" role="alert">{error}</div>}
      </div>
      <footer className="events-modal__footer">
        <span className="events-modal__count" aria-live="polite">Показано {events.length}{total !== null ? ` из ${total.toLocaleString('ru-RU')}` : ''}
          {limitRef.current >= 1000 && <small>Последние 1000 событий. Более ранние доступны в журнале участка.</small>}</span>
        {(hasMore || error || (loading && events.length === 0)) && <button type="button" className="ui-btn ui-btn--primary" disabled={loading} onClick={() => void load()}>
          {loading ? 'Загрузка…' : error ? 'Попробовать ещё раз' : 'Показать ещё'}</button>}
        {!hasMore && !loading && !error && events.length > 0 && limitRef.current < 1000 && <span className="events-modal__end">Все доступные события загружены</span>}
      </footer>
    </div>
  </div>;
};

export default EventsModal;
